import { useCallback, useEffect, useMemo, useState } from 'react';
import { DiscussionEntityRef, DiscussionMessage } from '../domain/types';
import { DiscussionService } from '../application/DiscussionService';

interface UseDiscussionControllerOptions {
  service: DiscussionService;
  ref: DiscussionEntityRef;
  currentUserId: string;
  currentUserName: string;
  liveEnabled?: boolean;
}

export const useDiscussionController = ({
  service,
  ref,
  currentUserId,
  currentUserName,
  liveEnabled = true,
}: UseDiscussionControllerOptions) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [messages, setMessages] = useState<DiscussionMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);

  const stableRef = useMemo(() => ({
    entityType: ref.entityType,
    entityId: String(ref.entityId),
    organizationId: ref.organizationId,
  }), [ref.entityId, ref.entityType, ref.organizationId]);

  const refKey = useMemo(() => `${stableRef.entityType}:${stableRef.entityId}`, [stableRef.entityId, stableRef.entityType]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const page = await service.load(stableRef, undefined, 100);
      setMessages(page.items);
    } catch (err: any) {
      setError(err?.message || 'Failed to load discussion.');
    } finally {
      setLoading(false);
    }
  }, [service, stableRef]);

  useEffect(() => {
    let disposed = false;

    const init = async () => {
      await load();
      if (disposed || !liveEnabled) {
        return;
      }

      try {
        await service.connectLive();
        await service.subscribe(stableRef);
      } catch {
        if (!disposed) {
          setError((current) => current ?? 'Live discussion is unavailable right now. Messages still work.');
        }
      }
    };

    const unsubscribeRealtime = service.onEvent((event) => {
      if (event.type !== 'messageCreated') {
        return;
      }

      if (`${event.message.entityType}:${event.message.entityId}` !== refKey) {
        return;
      }

      setMessages((previous) => {
        if (previous.some((item) => item.id === event.message.id)) {
          return previous;
        }
        return [...previous, event.message];
      });
    });

    void init();

    return () => {
      disposed = true;
      unsubscribeRealtime();
      void (async () => {
        try {
          await service.unsubscribe(stableRef);
        } catch {
          // Ignore unsubscription errors during cleanup.
        }

        if (liveEnabled) {
          try {
            await service.disconnectLive();
          } catch {
            // Ignore disconnect errors during cleanup.
          }
        }
      })();
    };
  }, [liveEnabled, load, refKey, service, stableRef]);

  const send = async () => {
    const text = draft.trim();
    if (!text) {
      return;
    }

    const tempId = `temp-${Date.now()}`;
    const pendingMessage: DiscussionMessage = {
      id: tempId,
      entityType: stableRef.entityType,
      entityId: stableRef.entityId,
      senderId: currentUserId,
      senderDisplayName: currentUserName,
      text,
      createdAt: new Date().toISOString(),
      pending: true,
      clientMessageId: tempId,
    };

    setSending(true);
    setError(null);
    setMessages((previous) => [...previous, pendingMessage]);
    setDraft('');

    try {
      const saved = await service.send(stableRef, { text, clientMessageId: tempId });
      setMessages((previous) => previous.map((item) => item.id === tempId ? saved : item));
    } catch (err: any) {
      setError(err?.message || 'Failed to send message.');
      setMessages((previous) => previous.filter((item) => item.id !== tempId));
      setDraft(text);
    } finally {
      setSending(false);
    }
  };

  return {
    loading,
    error,
    messages,
    draft,
    setDraft,
    sending,
    send,
    reload: load,
  };
};
